/**
 * The default values of one row template, edited in "editing template" mode above the template's body. Reads the
 * cached database (its schema and `rowTemplates`, kept fresh by `database.updated`); writes go to the server
 * (online only) and show at once (an optimistic overlay until the server's copy arrives).
 */
import type { Database, PropertyDef, PropertyValue, Row, RowTemplate } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import type { DatabaseCache } from "../logic/database.ts";
import { rowTemplateValues } from "../logic/templates.ts";
import type { LiveQuery, TemplatesApi } from "../ports.ts";
import { peopleOf, RelatedDatabases, type PersonOption, type RelationOption } from "./DatabaseSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

const COMPUTED = new Set(["created_time", "created_by", "edited_time", "edited_by"]);

export type RowTemplateState = {
  databaseId: string;
  templateId: string;
  status: "loading" | "ready" | "notFound";
  database?: Database;
  template?: RowTemplate;
  /** The defaults as a row (what the cell editors show). */
  row?: Row;
  /** Properties a template can preset (computed ones excluded), title first. */
  properties: PropertyDef[];
  people: PersonOption[];
  relationOptions: Record<string, RelationOption[]>;
  titles: Record<string, string>;
  readOnly: boolean;
  saving: boolean;
  error?: MessageDescriptor;
  tzOffsetMinutes: number;
};

export class RowTemplateViewModel extends ViewModel<RowTemplateState> {
  private readonly ws: WorkspaceViewModel;
  private readonly api: TemplatesApi;
  private readonly query: LiveQuery<DatabaseCache>;
  private readonly related: RelatedDatabases;
  /** Optimistic edits not yet confirmed (null = cleared). */
  private overlay: Record<string, PropertyValue | null> = {};

  constructor(ws: WorkspaceViewModel, databaseId: string, templateId: string, api: TemplatesApi, readOnly: boolean) {
    super({
      databaseId,
      templateId,
      status: "loading",
      properties: [],
      people: [],
      relationOptions: {},
      titles: {},
      readOnly,
      saving: false,
      tzOffsetMinutes: ws.deps.clock.tzOffsetMinutes(ws.deps.clock.now()),
    });
    this.ws = ws;
    this.api = api;
    this.query = ws.deps.data.database(ws.wsId, databaseId);
    this.related = new RelatedDatabases(ws, () => this.recompute());
    this.onDispose(this.query.subscribe(() => this.recompute()));
    this.onDispose(() => this.query.dispose());
    this.onDispose(() => this.related.dispose());
    this.onDispose(ws.subscribe(() => this.recompute()));
    this.recompute();
  }

  private recompute(): void {
    const q = this.query.getSnapshot();
    const cache = q.data;
    if (cache === undefined) {
      this.update({ status: q.status === "error" ? "notFound" : "loading" });
      return;
    }
    this.related.sync(cache.database);
    const db = cache.database;
    const template = (db.rowTemplates ?? []).find((t) => t.id === this.state.templateId);
    const next: RowTemplateState = {
      ...this.state,
      status: template === undefined ? (q.fetching ? "loading" : "notFound") : "ready",
      database: db,
      properties: db.properties.filter((p) => !COMPUTED.has(p.type)),
      people: peopleOf(this.ws.state.cache),
      relationOptions: this.related.options(cache),
      titles: this.related.titles(cache),
    };
    if (template === undefined) {
      delete next.template;
      delete next.row;
    } else {
      next.template = template;
      const values: Record<string, PropertyValue> = { ...rowTemplateValues(db, template) };
      for (const [id, v] of Object.entries(this.overlay)) {
        if (v === null) delete values[id];
        else values[id] = v;
      }
      next.row = { id: template.id, databaseId: db.id, rank: template.rank, values, createdBy: template.createdBy, createdAt: template.updatedAt, updatedBy: template.createdBy, updatedAt: template.updatedAt };
    }
    this.setState(next);
  }

  /** Sets (or clears, null) one default value. */
  async setValue(propertyId: string, value: PropertyValue | null): Promise<void> {
    if (this.state.readOnly || this.state.template === undefined) return;
    this.overlay = { ...this.overlay, [propertyId]: value };
    this.update({ saving: true });
    this.recompute();
    try {
      await this.api.updateRowTemplate(this.ws.wsId, this.state.templateId, { values: { [propertyId]: value } });
      await this.query.refresh();
      const { [propertyId]: _done, ...rest } = this.overlay;
      this.overlay = rest;
      const s = { ...this.state, saving: false };
      delete s.error;
      this.setState(s);
    } catch {
      const { [propertyId]: _failed, ...rest } = this.overlay;
      this.overlay = rest;
      this.update({ saving: false, error: msg("templates.rowTemplates.failed") });
    }
    this.recompute();
  }

  dismissError(): void {
    if (this.state.error === undefined) return;
    const s = { ...this.state };
    delete s.error;
    this.setState(s);
  }
}
